import React, { ReactNode } from 'react';
import { AuthProvider } from '../auth/auth-context';
import { BrowserRouter } from 'react-router-dom';
import { BuildConfigProvider, SiteConfigProvider } from '../config';
import { CmsFeatureFlagProvider } from '../content/cms-featureFlag-context/CmsFeatureFlagProvider';
import { FavoriteProductsProvider } from './favorite-products';
import { FeatureFlagProvider } from '../feature-flag';
import { HelmetProviderAsync } from '../content/helmet/HelmetProvider';
import { ImThemeProvider } from '@im/im-theme';
import { LicenseInfo } from '@mui/x-license-pro';
import { LocalizationProvider, InitLocalization } from '../localization';
import { LogoutConfigProvider } from '../logout';
import { MFOutlet } from '@im/ui-lib/mf-utils';
import { MuiProviders } from '@im/ui-lib/theme/MuiProviders';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RecoilRoot } from 'recoil';
import { ScopeProvider } from './scope/ScopeProvider';
import { PageLoadMetricsProvider } from './dd-custom-actions/PageLoadMetrics/PageLoadMetricsProvider';
import { SameOriginApiSwitch } from '../query/SameOriginApiSwitch';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // for dev
      retry: 1, // default is 3
    },
  },
});

type Props = {
  basename?: string;
  children: ReactNode;
  envVars: any;
  mfOutlets?: MFOutlet[];
};

LicenseInfo.setLicenseKey(process.env.INGRAM__MUI_PRO_KEY ?? '');

export function AppProviders(props: Props) {
  const { basename, children, envVars, mfOutlets = [] } = props;
  return (
    // <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ScopeProvider scope="cep_app">
        <RecoilRoot>
          <BuildConfigProvider envVars={envVars} mfOutlets={mfOutlets}>
            <SiteConfigProvider>
              <LocalizationProvider>
                <ImThemeProvider>
                  <MuiProviders>
                    <BrowserRouter basename={basename}>
                      <PageLoadMetricsProvider>
                        <AuthProvider>
                          <InitLocalization {...props}>
                            <LogoutConfigProvider>
                              <FeatureFlagProvider>
                                <CmsFeatureFlagProvider>
                                  <HelmetProviderAsync>
                                    <FavoriteProductsProvider>
                                      <SameOriginApiSwitch />
                                      {children}
                                    </FavoriteProductsProvider>
                                  </HelmetProviderAsync>
                                </CmsFeatureFlagProvider>
                              </FeatureFlagProvider>
                            </LogoutConfigProvider>
                          </InitLocalization>
                        </AuthProvider>
                      </PageLoadMetricsProvider>
                    </BrowserRouter>
                  </MuiProviders>
                </ImThemeProvider>
              </LocalizationProvider>
            </SiteConfigProvider>
          </BuildConfigProvider>
        </RecoilRoot>
      </ScopeProvider>
    </QueryClientProvider>
    // </React.StrictMode>
  );
}
